import { BaseApiClient } from './base-client'
import {
  type ApiResponse,
  type NodeAgentRecord,
  type NodeGraph,
  API_ROUTES,
  interpolateRoute,
} from '@super/shared-types'
import { withErrorHandling } from '../utils'

export interface CreateNodeAgentRequest {
  name: string
  graph: NodeGraph
}

export interface UpdateNodeAgentRequest {
  name?: string
  active?: boolean
  graph?: NodeGraph
}

export interface DispatchNodeAgentRequest {
  phone: string
  from?: string
  variableValues?: Record<string, string>
}

// Controllers return raw records, but the global ObservabilityInterceptor
// wraps every response in the {success, data} envelope — unwrap .data here.
export class NodeAgentsApiClient extends BaseApiClient {
  async list(): Promise<NodeAgentRecord[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeAgentRecord[]>>(
          API_ROUTES.NODE_AGENTS.GET_ALL,
        )
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching node agents',
      },
    )
    return result ?? []
  }

  async get(id: string): Promise<NodeAgentRecord | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeAgentRecord>>(
          interpolateRoute(API_ROUTES.NODE_AGENTS.GET, { id }),
        )
        return response.data ?? null
      },
      {
        on404: 'ignore',
        errorContext: 'Error fetching node agent',
      },
    )
  }

  async create(dto: CreateNodeAgentRequest): Promise<NodeAgentRecord | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeAgentRecord>>(
          API_ROUTES.NODE_AGENTS.CREATE,
          { method: 'POST', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error creating node agent' },
    )
  }

  async update(
    id: string,
    dto: UpdateNodeAgentRequest,
  ): Promise<NodeAgentRecord | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeAgentRecord>>(
          interpolateRoute(API_ROUTES.NODE_AGENTS.UPDATE, { id }),
          { method: 'PATCH', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error updating node agent' },
    )
  }

  async remove(id: string): Promise<boolean> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<{ deleted: boolean }>>(
          interpolateRoute(API_ROUTES.NODE_AGENTS.DELETE, { id }),
          { method: 'DELETE' },
        )
        return response.data?.deleted === true
      },
      { errorContext: 'Error deleting node agent' },
    )
    return result === true
  }

  async dispatch(
    id: string,
    dto: DispatchNodeAgentRequest,
  ): Promise<{ roomName: string } | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<{ roomName: string }>>(
          interpolateRoute(API_ROUTES.NODE_AGENTS.DISPATCH, { id }),
          { method: 'POST', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error dispatching node agent call' },
    )
  }
}

export const nodeAgentsApiClient = new NodeAgentsApiClient()
